import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/电影" },
  { label: "电视剧", to: "/category/电视剧" },
  { label: "全部", to: "/category/全部" },
  { label: "专题", to: "/#topics" },
  { label: "关于", to: "/about" },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword("");
  };

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    if (to.startsWith("/category/")) {
      const type = decodeURIComponent(to.replace("/category/", ""));
      return location.pathname.startsWith("/category/") && location.pathname.includes(encodeURIComponent(type));
    }
    return location.pathname.startsWith(to);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200"
          : "bg-background-50 border-b border-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-[68px] gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
            <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-film-fill text-accent-400 text-xl" />
            </span>
            <span className="font-heading text-lg md:text-xl font-extrabold text-foreground-950 tracking-tight whitespace-nowrap">
              大师兄<span className="text-accent-500">影视</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 flex-1 justify-center">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors duration-200 cursor-pointer ${
                  isActive(item.to)
                    ? "text-primary-600 bg-primary-100/70"
                    : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <form onSubmit={handleSearch} className="hidden md:flex items-center gap-2 shrink-0">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm" />
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视名称"
                className="w-44 lg:w-56 h-9 pl-9 pr-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
              />
            </div>
            <button
              type="submit"
              className="h-9 px-4 rounded-md bg-accent-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
            >
              搜索
            </button>
          </form>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-md text-foreground-800 cursor-pointer"
            aria-label="展开菜单"
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 animate-fade-in">
          <div className="px-4 py-3">
            <form onSubmit={handleSearch} className="flex items-center gap-2 mb-3">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm" />
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索影视名称"
                  className="w-full h-10 pl-9 pr-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-400"
                />
              </div>
              <button
                type="submit"
                className="h-10 px-4 rounded-md bg-accent-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </form>
            <nav className="flex flex-col">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`px-3 py-3 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                    isActive(item.to)
                      ? "text-primary-600 bg-primary-100/70"
                      : "text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}